import React, { createContext, useContext, useEffect, useState } from 'react';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import { apiClient } from '../common/apiClient';
import { preloadProfilePicture } from '../utils/profilePicture';

interface User {
  name: string;
  email: string;
  id: string;
}

export interface AuthContextType {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: User | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  isLoggingIn: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Initialize apiClient with MSAL instance
  useEffect(() => {
    apiClient.setMsalInstance(instance);
  }, [instance]);

  // Fetch user profile from Microsoft Graph
  const fetchUserProfile = async (accessToken: string): Promise<User | null> => {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const profile = await response.json();

      const user = {
        name: profile.displayName || profile.name || 'Unknown User',
        email: profile.mail || profile.userPrincipalName || '',
        id: profile.id || '',
      };

      // Preload profile picture in background (non-blocking)
      preloadProfilePicture(user.id, user.name);

      return user;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      return null;
    }
  };

  // Handle authentication state changes
  useEffect(() => {
    const handleAuth = async () => {
      // If MSAL is still loading, wait
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      // If already initialized, don't run again unless accounts change
      if (isInitialized && user) {
        return;
      }

      try {
        const activeAccount = instance.getActiveAccount();
        
        if (activeAccount) {
          // Try to get access token silently
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              ...loginRequest,
              account: activeAccount,
            });
            
            const userProfile = await fetchUserProfile(tokenResponse.accessToken);
            if (userProfile) {
              setUser(userProfile);
            }
          } catch (tokenError) {
            console.error('Failed to acquire token silently:', tokenError);
            // User might need to login again
            setUser(null);
          }
        } else if (accounts.length > 0) {
          // Set the first account as active
          instance.setActiveAccount(accounts[0]);
          
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            });
            
            const userProfile = await fetchUserProfile(tokenResponse.accessToken);
            if (userProfile) {
              setUser(userProfile);
            }
          } catch (tokenError) {
            console.error('Failed to acquire token silently:', tokenError);
            setUser(null);
          }
        } else if (!isInitialized) {
          // Only attempt SSO once on initial load
          try {
            // Attempt SSO silent login to detect if user is already authenticated with Microsoft
            const ssoRequest = {
              scopes: ['User.Read'],
              // Don't specify loginHint to let user choose if multiple accounts
            };
            
            const ssoResponse = await instance.ssoSilent(ssoRequest);
            
            if (ssoResponse) {
              const userProfile = await fetchUserProfile(ssoResponse.accessToken);
              if (userProfile) {
                setUser(userProfile);
              }
            }
          } catch (ssoError: any) {
            // SSO failed, user needs to login manually (this is expected behavior)
            console.log('SSO not available, user needs to login');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth handling error:', error);
        setUser(null);
      } finally {
        setIsInitialized(true);
        setIsLoggingIn(false);
      }
    };

    // Add a small delay to prevent rapid updates
    const timer = setTimeout(handleAuth, 100);
    return () => clearTimeout(timer);
  }, [accounts, inProgress, instance, isInitialized, user]);

  // Pre-cache Salesforce contact after user is authenticated
  useEffect(() => {
    const cacheContact = async () => {
      if (isAuthenticated && user && isInitialized) {
        try {
          // Trigger contact cache by calling the endpoint
          await apiClient.get('/api/salesforce/my-contact');
        } catch (error) {
          // Silent fail - contact will be fetched on next API call
          console.log('Contact pre-cache skipped');
        }
      }
    };

    cacheContact();
  }, [isAuthenticated, user, isInitialized]);

  // Handle redirect response
  useEffect(() => {
    const handleRedirect = async () => {
      if (inProgress !== InteractionStatus.None) return;

      try {
        const response = await instance.handleRedirectPromise();
        if (response && response.account) {
          instance.setActiveAccount(response.account);
          
          // Get user profile
          const userProfile = await fetchUserProfile(response.accessToken);
          if (userProfile) {
            setUser(userProfile);
          }
          
          // Check for saved redirect URL
          const savedUrl = sessionStorage.getItem('redirectUrl');
          if (savedUrl && savedUrl !== '/') {
            sessionStorage.removeItem('redirectUrl');
            // Use window.location for a clean redirect
            window.location.href = savedUrl;
          }
        }
      } catch (error: any) {
        console.error('Redirect handling error:', error);
        
        // Handle cache quota exceeded error
        if (error?.errorCode === 'cache_quota_exceeded' || 
            error?.message?.includes('cache_quota_exceeded')) {
          console.warn('Cache quota exceeded, clearing cache...');
          
          try {
            // Clear MSAL cache
            await instance.clearCache();
            
            // Clear localStorage items related to MSAL
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && (key.includes('msal') || key.includes('login.microsoftonline.com'))) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            
            setError('Authentication cache was cleared. Please sign in again.');
          } catch (clearError) {
            console.error('Failed to clear cache:', clearError);
            setError('Cache is full. Please clear your browser cache or try in a private window.');
          }
        } else {
          setError('Login redirect failed');
        }
      }
    };

    handleRedirect();
  }, [instance, inProgress]);

  // Login function using Azure AD redirect
  const login = async () => {
    // Prevent multiple simultaneous login attempts
    if (isLoggingIn || inProgress !== InteractionStatus.None) {
      return;
    }

    try {
      setError(null);
      setIsLoggingIn(true);
      
      // Save current URL for redirect after login
      const currentUrl = window.location.pathname + window.location.search;
      if (currentUrl !== '/' && currentUrl !== '/login') {
        sessionStorage.setItem('redirectUrl', currentUrl);
      }
      
      // Check if there's already an interaction in progress
      const activeAccount = instance.getActiveAccount();
      const accounts = instance.getAllAccounts();
      
      // If there are existing accounts but no active account, set one
      if (accounts.length > 0 && !activeAccount) {
        instance.setActiveAccount(accounts[0]);
      }
      
      // Proceed with login
      await instance.loginRedirect(loginRequest);
    } catch (error: any) {
      console.error('Login failed:', error);
      
      // Handle specific interaction_in_progress error
      if (error?.errorCode === 'interaction_in_progress') {
        // Clear everything and reload
        await instance.clearCache();
        sessionStorage.clear();
        window.location.reload();
      } else if (error?.errorCode === 'cache_quota_exceeded') {
        // Handle cache quota exceeded
        try {
          await instance.clearCache();
          // Clear localStorage items related to MSAL
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('msal') || key.includes('login.microsoftonline.com'))) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          
          // Try login again
          await instance.loginRedirect(loginRequest);
        } catch (retryError) {
          setError('Failed to login. Please clear your browser cache and try again.');
          setIsLoggingIn(false);
        }
      } else {
        setError('Login failed. Please try again.');
        setIsLoggingIn(false);
      }
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setError(null);

      // Clear Salesforce contact cache on backend before logging out
      if (user?.email) {
        try {
          await apiClient.post('/api/salesforce/refresh-contact');
        } catch (error) {
          // Silent fail - cache will be cleared anyway on next login
          console.log('Contact cache clear skipped');
        }
      }

      setUser(null);

      // Clear any stored redirect URL
      sessionStorage.removeItem('redirectUrl');

      // Clear MSAL cache
      await instance.clearCache();
      
      // Use logoutRedirect with account parameter to avoid account picker
      const account = instance.getActiveAccount();
      if (account) {
        await instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin,
          account: account,
        });
      } else {
        // If no active account, just redirect to login page
        await instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin,
        });
      }
    } catch (error) {
      console.error('Logout failed:', error);
      setError('Logout failed. Please try again.');
    }
  };

  const value = {
    isAuthenticated,
    isInitialized,
    user,
    login,
    logout,
    error,
    isLoggingIn,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};